/**
 * Admin users page — lists platform users and starts read-only impersonation.
 *
 * Route visibility is a convenience only. The API remains the authorization boundary.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkRegular, DismissCircleRegular, EyeRegular, SearchRegular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { impersonationService, type AdminUser } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useTopBarBreadcrumbs, useTopBarTitle } from '../contexts/TopBarContext';
import AdminPageLayout, { AdminPageContent } from '../components/AdminPageLayout';
import { AdminDataGrid, AdminDataGridEmpty, AdminPagination } from '../components/AdminDataGrid';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  search: {
    width: '100%',
    maxWidth: '600px',
  },
  content: {
    position: 'relative',
  },
  description: {
    display: 'block',
    marginBottom: '16px',
    color: tokens.colorNeutralForeground3,
  },
  error: {
    marginBottom: '16px',
    color: tokens.colorPaletteRedForeground1,
  },
  loadingBar: {
    minHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  userColumn: {
    width: '180px',
    minWidth: '180px',
  },
  emailColumn: {
    width: '240px',
    minWidth: '240px',
  },
  roleColumn: {
    width: '72px',
    minWidth: '72px',
  },
  statusColumn: {
    width: '88px',
    minWidth: '88px',
  },
  resourceCountCell: {
    width: '96px',
    minWidth: '96px',
    maxWidth: '96px',
    textAlign: 'center',
    whiteSpace: 'normal',
  },
  dialogDescription: {
    color: tokens.colorNeutralForeground3,
    marginBottom: '16px',
  },
  startButton: {
    whiteSpace: 'nowrap',
  },
});

const AdminUsersPage: React.FC = () => {
  const styles = useStyles();
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();

  useTopBarTitle('Users');
  useTopBarBreadcrumbs([
    { text: 'Admin', to: '/admin' },
    { text: 'Users' },
  ]);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [reason, setReason] = useState('');
  const [impersonating, setImpersonating] = useState(false);
  const [statusUser, setStatusUser] = useState<AdminUser | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setAppliedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadUsers = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    try {
      setLoading(true);
      setError(null);
      const result = await impersonationService.listUsers(
        page,
        PAGE_SIZE,
        appliedSearch || undefined,
      );

      if (requestId !== requestIdRef.current) return;
      setUsers(result.users);
      setTotal(result.total);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setError('Failed to load users');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [page, appliedSearch]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const handleViewAsUser = (user: AdminUser) => {
    setSelectedUser(user);
    setReason('');
    setDialogOpen(true);
  };

  const handleStartImpersonation = async () => {
    if (!selectedUser) return;

    setImpersonating(true);
    const success = await startImpersonation(selectedUser.id, reason || undefined);
    setImpersonating(false);

    if (success) {
      setDialogOpen(false);
      navigate('/overview', { replace: true });
    }
  };

  const handleUpdateStatus = async () => {
    if (!statusUser) return;
    setUpdatingStatus(true);
    setError(null);
    try {
      const updated = await impersonationService.updateUserStatus(statusUser.id, !statusUser.isActive);
      setUsers((current) => current.map((user) => user.id === updated.id ? updated : user));
      setStatusUser(null);
    } catch {
      setError(`Failed to ${statusUser.isActive ? 'disable' : 'enable'} user`);
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <AdminPageLayout>
      <AdminPageContent>
        <div className={styles.toolbar}>
          <Input
            className={styles.search}
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(_, data) => setSearchInput(data.value)}
            contentBefore={<SearchRegular />}
            aria-label="Search users"
          />
        </div>

        <div className={styles.content}>
        <Text className={styles.description}>
          Manage platform users, access status, and impersonation for support and debugging.
        </Text>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.loadingBar} aria-live="polite">
          {loading && <Spinner size="tiny" label="Loading users..." />}
        </div>

        <AdminDataGrid>
          <Table aria-label="Platform users">
            <TableHeader>
              <TableRow>
                <TableHeaderCell className={styles.userColumn}>User</TableHeaderCell>
                <TableHeaderCell className={styles.emailColumn}>Email</TableHeaderCell>
                <TableHeaderCell className={styles.roleColumn}>Role</TableHeaderCell>
                <TableHeaderCell className={styles.statusColumn}>Status</TableHeaderCell>
                <TableHeaderCell>Auth</TableHeaderCell>
                <TableHeaderCell className={styles.resourceCountCell}>Connections</TableHeaderCell>
                <TableHeaderCell className={styles.resourceCountCell}>Data models</TableHeaderCell>
                <TableHeaderCell className={styles.resourceCountCell}>Dashboards</TableHeaderCell>
                <TableHeaderCell>Created</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className={styles.userColumn}>
                    <div className={styles.userCell}>
                      <Avatar
                        name={user.name || user.email}
                        image={user.avatarUrl ? { src: user.avatarUrl } : undefined}
                        size={28}
                      />
                      <span>{user.name || '—'}</span>
                    </div>
                  </TableCell>
                  <TableCell className={styles.emailColumn}>{user.email}</TableCell>
                  <TableCell className={styles.roleColumn}>
                    <Badge
                      appearance="filled"
                      color={user.role === 'admin' ? 'danger' : 'informative'}
                    >
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell className={styles.statusColumn}>
                    <Badge
                      appearance="outline"
                      color={user.isActive ? 'success' : 'danger'}
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>{user.authProvider}</TableCell>
                  <TableCell className={styles.resourceCountCell}>{user._count.connections}</TableCell>
                  <TableCell className={styles.resourceCountCell}>{user._count.dataModels}</TableCell>
                  <TableCell className={styles.resourceCountCell}>{user._count.dashboards}</TableCell>
                  <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {user.role !== 'admin' && <>
                      {user.isActive && (
                        <Button appearance="subtle" icon={<EyeRegular />} size="small" onClick={() => handleViewAsUser(user)}>
                          View as user
                        </Button>
                      )}
                      <Button
                        appearance="subtle"
                        icon={user.isActive ? <DismissCircleRegular /> : <CheckmarkRegular />}
                        size="small"
                        onClick={() => setStatusUser(user)}
                      >
                        {user.isActive ? 'Disable' : 'Enable'}
                      </Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

        </AdminDataGrid>

        {!loading && users.length === 0 && (
          <AdminDataGridEmpty>No users match the current search.</AdminDataGridEmpty>
        )}

        <AdminPagination
          totalLabel={`${total} users total`}
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          loading={loading}
          onPageChange={setPage}
        />
        </div>
      </AdminPageContent>

      <Dialog open={dialogOpen} onOpenChange={(_, data) => setDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>View as {selectedUser?.name || selectedUser?.email}</DialogTitle>
            <DialogContent>
              <p>
                <strong>{selectedUser?.email}</strong>
              </p>
              <p className={styles.dialogDescription}>
                You are about to enter read-only impersonation mode. You will see the
                application as this user sees it. All actions will be logged.
              </p>
              <Textarea
                placeholder="Reason for impersonation (optional)"
                value={reason}
                onChange={(_, data) => setReason(data.value)}
                style={{ width: '100%' }}
                rows={2}
              />
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDialogOpen(false)}
                disabled={impersonating}
              >
                Cancel
              </Button>
              <Button
                className={styles.startButton}
                appearance="primary"
                onClick={handleStartImpersonation}
                disabled={impersonating}
                icon={impersonating ? <Spinner size="tiny" /> : <EyeRegular />}
              >
                {impersonating ? 'Starting...' : 'Start impersonation'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={statusUser !== null} onOpenChange={(_, data) => !data.open && !updatingStatus && setStatusUser(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{statusUser?.isActive ? 'Disable' : 'Enable'} user</DialogTitle>
            <DialogContent>
              {statusUser?.isActive
                ? `Disable ${statusUser.email}? Existing sessions will be rejected on their next API request.`
                : `Enable ${statusUser?.email}? They will be able to sign in again.`}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" disabled={updatingStatus} onClick={() => setStatusUser(null)}>Cancel</Button>
              <Button
                appearance="primary"
                disabled={updatingStatus}
                icon={updatingStatus ? <Spinner size="tiny" /> : undefined}
                onClick={() => void handleUpdateStatus()}
              >
                {statusUser?.isActive ? 'Disable user' : 'Enable user'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </AdminPageLayout>
  );
};

export default AdminUsersPage;